import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SearchableSelect from "../components/SearchableSelect";
import tari from "../data/tari";
import oraseRomania from "../data/oraseRomania";
import companiiExpozanti from "../data/companiiExpozanti.json";

const generateRandomId = (length = 30) => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  return Array.from({ length }, () => chars.charAt(Math.floor(Math.random() * chars.length))).join("");
};

const RegistrationForm = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    category: "select",
    personType: "", // doar pentru Vizitator: "Persoană fizică" / "Companie"
    company: "",
    country: "",
    city: "",
    acceptTerms: false,
    acceptGDPR: false,
  });

  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const navigate = useNavigate();

  const isVisitor = formData.category === "Vizitator";
  const isExhibitor = formData.category === "Expozant";
  const isRomania = formData.country === "România";
  const isVisitorCompany = isVisitor && formData.personType === "Companie";

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: type === "checkbox" ? checked : value };
      // La schimbarea categoriei, resetăm câmpurile dependente
      if (name === "category") {
        next.personType = "";
        next.company = "";
        next.country = "";
        next.city = "";
      }
      // Vizitator persoană fizică => fără nume companie
      if (name === "personType" && value !== "Companie") next.company = "";
      return next;
    });
  };

  // Actualizează un câmp din dropdown-urile cu căutare
  const handleSelect = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // Dacă se schimbă țara și nu mai e România, golim orașul
      if (field === "country" && value !== "România") next.city = "";
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.acceptGDPR || !formData.acceptTerms) {
      setSuccessMsg(t("gdpr_termeni_obligatoriu"));
      return;
    }

    setSubmitting(true);
    setSuccessMsg("");
    const customId = generateRandomId(30);

    try {
      const response = await fetch("https://api.evenimente-ara.ro/api/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          category: formData.category,
          company: formData.company || null,
          acceptTerms: formData.acceptTerms,
          acceptGDPR: formData.acceptGDPR,
          attendees: 1,
          col1: customId,
          col3: formData.country || null, // Țară
          col4: isExhibitor && isRomania ? formData.city || null : null, // Oraș (doar expozant + România)
          col5: isVisitor ? formData.personType || null : null, // Tip participant (vizitator)
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMsg(t("inscriere_success"));
        setSubmitting(false);
        // Folosim codul returnat de server (invitația e legată de el)
        navigate("/success", { state: { qrData: data.customId || customId } });
      } else {
        setSuccessMsg(`❗ Eroare: ${data.error || "Ceva nu a mers bine."}`);
        setSubmitting(false);
      }
    } catch (error) {
      setSuccessMsg("❗ Eroare la conexiunea cu serverul.");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Nume Prenume */}
      <div>
        <label className="block text-sm font-semibold text-[#003B5C]">{t("fullName")}</label>
        <input
          type="text"
          name="fullName"
          value={formData.fullName}
          onChange={handleChange}
          className="mt-1 block w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-[15px] text-[#003B5C] shadow-sm transition-all placeholder:text-gray-400 focus:border-[#0066A6] focus:outline-none focus:ring-4 focus:ring-[#0066A6]/10"
          required
        />
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-semibold text-[#003B5C]">Email</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className="mt-1 block w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-[15px] text-[#003B5C] shadow-sm transition-all placeholder:text-gray-400 focus:border-[#0066A6] focus:outline-none focus:ring-4 focus:ring-[#0066A6]/10"
          required
        />
      </div>

      {/* Telefon */}
      <div>
        <label className="block text-sm font-semibold text-[#003B5C]">{t("phone")}</label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className="mt-1 block w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-[15px] text-[#003B5C] shadow-sm transition-all placeholder:text-gray-400 focus:border-[#0066A6] focus:outline-none focus:ring-4 focus:ring-[#0066A6]/10"
          required
        />
      </div>

      {/* Categorie: doar Vizitator și Expozant */}
      <div>
        <label className="block text-sm font-semibold text-[#003B5C]">{t("category")}</label>
        <select
          name="category"
          value={formData.category}
          onChange={handleChange}
          className="mt-1 block w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-[15px] text-[#003B5C] shadow-sm transition-all placeholder:text-gray-400 focus:border-[#0066A6] focus:outline-none focus:ring-4 focus:ring-[#0066A6]/10"
          required
        >
          <option value="select" disabled>{t("select")}</option>
          <option value="Vizitator">{t("visitor")}</option>
          <option value="Expozant">{t("exhibitor")}</option>
        </select>
      </div>

      {/* VIZITATOR: tip participant (persoană fizică / companie) + țară */}
      {isVisitor && (
        <>
          <div>
            <label className="block text-sm font-semibold text-[#003B5C]">{t("person_type")}</label>
            <select
              name="personType"
              value={formData.personType}
              onChange={handleChange}
              className="mt-1 block w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-[15px] text-[#003B5C] shadow-sm transition-all focus:border-[#0066A6] focus:outline-none focus:ring-4 focus:ring-[#0066A6]/10"
              required
            >
              <option value="" disabled>{t("select")}</option>
              <option value="Persoană fizică">{t("individual")}</option>
              <option value="Companie">{t("company")}</option>
            </select>
          </div>

          {/* Dacă e companie, numele companiei devine obligatoriu */}
          {isVisitorCompany && (
            <div>
              <label className="block text-sm font-semibold text-[#003B5C]">{t("company_name")}</label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="mt-1 block w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-[15px] text-[#003B5C] shadow-sm transition-all placeholder:text-gray-400 focus:border-[#0066A6] focus:outline-none focus:ring-4 focus:ring-[#0066A6]/10"
                required
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-[#003B5C]">{t("country")}</label>
            <SearchableSelect
              options={tari}
              value={formData.country}
              onChange={(val) => handleSelect("country", val)}
              placeholder={t("select_country")}
              required
              name="country"
            />
          </div>
        </>
      )}

      {/* EXPOZANT: companie (din fișier) + țară + oraș (dacă România) */}
      {isExhibitor && (
        <>
          <div>
            <label className="block text-sm font-semibold text-[#003B5C]">{t("company_name")}</label>
            <SearchableSelect
              options={companiiExpozanti}
              value={formData.company}
              onChange={(val) => handleSelect("company", val)}
              placeholder={t("select_company")}
              required
              name="company"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#003B5C]">{t("country")}</label>
            <SearchableSelect
              options={tari}
              value={formData.country}
              onChange={(val) => handleSelect("country", val)}
              placeholder={t("select_country")}
              required
              name="country"
            />
          </div>
          {isRomania && (
            <div>
              <label className="block text-sm font-semibold text-[#003B5C]">{t("city")}</label>
              <SearchableSelect
                options={oraseRomania}
                value={formData.city}
                onChange={(val) => handleSelect("city", val)}
                placeholder={t("select_city")}
                required
                name="city"
              />
            </div>
          )}
        </>
      )}

      {/* GDPR & Terms */}
      <div className="space-y-2">
        <label className="flex items-center space-x-2 text-sm text-[#003B5C]">
          <input
            type="checkbox"
            name="acceptGDPR"
            checked={formData.acceptGDPR}
            onChange={handleChange}
            className="accent-[#0066A6]"
            required
          />
          <span>
            {t("accept_gdpr")}{" "}
            <a href="https://ara.ro/sp/politica-gdpr" className="font-bold text-[13px]">
              {t("gdpr_policy")}
            </a>
          </span>
        </label>
        <label className="flex items-center space-x-2 text-sm text-[#003B5C]">
          <input
            type="checkbox"
            name="acceptTerms"
            checked={formData.acceptTerms}
            onChange={handleChange}
            className="accent-[#0066A6]"
            required
          />
          <span>{t("accept_terms")}</span>
        </label>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={submitting}
        className="flex w-full items-center justify-center rounded-xl bg-[#0066A6] py-3.5 font-semibold text-white shadow-md shadow-[#0066A6]/25 transition-all hover:bg-[#005377] hover:shadow-lg active:scale-[0.99] disabled:opacity-70"
      >
        {submitting ? (
          <div className="flex items-center space-x-2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
            <span>{t("sending")}</span>
          </div>
        ) : (
          t("register1")
        )}
      </button>

      {/* Mesaj succes/eroare */}
      {successMsg && (
        <div
          className={`flex items-start gap-2.5 rounded-xl border px-4 py-3 text-sm font-medium ${
            successMsg.includes("❗")
              ? "border-red-200 bg-red-50 text-red-700"
              : "border-green-200 bg-green-50 text-green-700"
          }`}
        >
          <span>{successMsg}</span>
        </div>
      )}
    </form>
  );
};

export default RegistrationForm;
